import Logo from "../components/Logo";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Wallet,
  Coins,
  PackageCheck,
  TrendingUp,
  FileSpreadsheet,
  PlusCircle,
  Calculator,
  Barcode,
  CheckCircle2,
  AlertCircle,
  Truck,
  Building,
  ChevronRight,
  Search,
  Terminal,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import { calculateMockRates, MOCK_TRANSACTIONS } from "../data/shipmentData";

export default function Dashboard({ shipments, onAddShipment, onTabChange }) {
  const [activeSubTab, setActiveSubTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  // Wallet state
  const [walletBalance, setWalletBalance] = useState(13430);
  const [transactions, setTransactions] = useState(MOCK_TRANSACTIONS);
  const [topUpAmount, setTopUpAmount] = useState("");

  // Shipping form fields
  const [senderName, setSenderName] = useState("Apex Electronics");
  const [senderMobile, setSenderMobile] = useState("9876543210");
  const [senderAddress, setSenderAddress] = useState(
    "Plot 42, Electronic City Phase 1, Bangalore",
  );
  const [originCity, setOriginCity] = useState("Bangalore");
  const [originPincode, setOriginPincode] = useState("560100");

  const [receiverName, setReceiverName] = useState("");
  const [receiverMobile, setReceiverMobile] = useState("");
  const [receiverAddress, setReceiverAddress] = useState("");
  const [destinationCity, setDestinationCity] = useState("Mumbai");
  const [destinationPincode, setDestinationPincode] = useState("400050");

  const [weight, setWeight] = useState(1.5);
  const [serviceType, setServiceType] = useState("Express Parcel");
  const [paymentType, setPaymentType] = useState("Prepaid");
  const [codAmount, setCodAmount] = useState(0);

  // Rate estimation object
  const [rateEstimate, setRateEstimate] = useState(() =>
    calculateMockRates({
      originPincode: "560100",
      destinationPincode: "400050",
      weight: 1.5,
      serviceType: "Express Parcel",
      paymentType: "Prepaid",
    }),
  );

  // Label printing state
  const [bookedLabel, setBookedLabel] = useState(null);

  // Handle custom dynamic navigation from external triggers (e.g. Rate Estimate card on Home page)
  useEffect(() => {
    const handleNav = (e) => {
      const customEvent = e;
      if (customEvent.detail?.tab) {
        setActiveSubTab(customEvent.detail.tab);
      }
      if (customEvent.detail?.origin)
        setOriginPincode(customEvent.detail.origin);
      if (customEvent.detail?.dest)
        setDestinationPincode(customEvent.detail.dest);
      if (customEvent.detail?.weight)
        setWeight(parseFloat(customEvent.detail.weight) || 1.0);
    };

    window.addEventListener("jpsy-navigate", handleNav);
    return () => window.removeEventListener("jpsy-navigate", handleNav);
  }, []);

  // Recalculate rates dynamically when input dimensions shift
  useEffect(() => {
    const est = calculateMockRates({
      originPincode: originPincode || "560100",
      destinationPincode: destinationPincode || "400050",
      weight: weight || 1.0,
      serviceType,
      paymentType,
      codAmount,
    });
    setRateEstimate(est);
  }, [
    originPincode,
    destinationPincode,
    weight,
    serviceType,
    paymentType,
    codAmount,
  ]);

  // Handle UPI Topup
  const handleTopUp = (e) => {
    e.preventDefault();
    const amt = parseFloat(topUpAmount);
    if (!amt || amt <= 0) return;

    setWalletBalance((prev) => prev + amt);
    const newTxn = {
      id: `TXN-${Math.floor(Math.random() * 900000) + 100000}`,
      date: new Date().toLocaleString(),
      type: "credit",
      amount: amt,
      description: "UPI Wallet Top Up - Digital Merchant Gateway",
      status: "success",
    };
    setTransactions((prev) => [newTxn, ...prev]);
    setTopUpAmount("");
  };

  // Submit shipment booking
  const handleBookShipment = (e) => {
    e.preventDefault();
    if (
      !receiverName ||
      !receiverMobile ||
      !receiverAddress ||
      !destinationPincode
    ) {
      alert("Please fill all receiver details before booking.");
      return;
    }

    if (paymentType === "Prepaid" && walletBalance < rateEstimate.total) {
      alert(
        "Insufficient wallet balance to book prepaid shipment. Please top up your wallet first.",
      );
      return;
    }

    // Deduct wallet balance if prepaid
    if (paymentType === "Prepaid") {
      setWalletBalance((prev) => prev - rateEstimate.total);
    }

    // Generate random AWB
    const newAwb = `JPSY${Math.floor(Math.random() * 9000000) + 1000000}`;
    const newOrderId = `ORD-${Math.floor(Math.random() * 900000) + 100000}`;
    const newLrn = `LRN-${Math.floor(Math.random() * 900000) + 100000}`;

    // Standard booking structures
    const bookedShipment = {
      awb: newAwb,
      orderId: newOrderId,
      lrn: newLrn,
      senderMobile,
      receiverMobile,
      senderName,
      senderAddress: `${senderAddress}, ${originCity} - ${originPincode}`,
      receiverName,
      receiverAddress: `${receiverAddress}, ${destinationCity} - ${destinationPincode}`,
      originCity,
      destinationCity,
      originPincode,
      destinationPincode,
      weight,
      dimensions: "25x20x15 cm",
      status: "booked",
      currentLocation: {
        lat: 12.9716,
        lng: 77.5946,
        name: `${originCity} Sorter Gateway`,
      },
      serviceType,
      paymentType,
      codAmount: paymentType === "COD" ? codAmount : 0,
      bookingDate:
        new Date().toLocaleDateString() +
        " " +
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      estimatedDelivery: new Date(
        Date.now() + rateEstimate.etaDays * 24 * 60 * 60 * 1000,
      )
        .toISOString()
        .split("T")[0],
      transportType:
        weight > 10
          ? "truck"
          : serviceType === "International"
            ? "flight"
            : "bike",
      routeCoordinates: [
        {
          lat: 12.9716,
          lng: 77.5946,
          name: `${originCity} Sorter Gateway`,
          isCompleted: true,
        },
        {
          lat: 19.076,
          lng: 72.8777,
          name: `${destinationCity} Delivery Center`,
          isCompleted: false,
        },
      ],
      milestones: [
        {
          status: "booked",
          title: "Shipment Booked",
          location: `${originCity} Sortation Hub`,
          timestamp: new Date().toLocaleString(),
          description: "Shipment registered. Core sorting pending.",
          completed: true,
        },
        {
          status: "dispatched",
          title: "Manifested & Dispatched",
          location: `${originCity} Sortation Hub`,
          timestamp: "Pending",
          description: "Consignment packaged and sorting queued.",
          completed: false,
        },
        {
          status: "in_transit",
          title: "In Transit",
          location: "Inter-state Supply Chain Network",
          timestamp: "Pending",
          description: "Checked out on linehaul container.",
          completed: false,
        },
        {
          status: "reached_hub",
          title: "Reached Destination Hub",
          location: `${destinationCity} Hub`,
          timestamp: "Pending",
          description: "Awaiting sorting scan.",
          completed: false,
        },
        {
          status: "out_for_delivery",
          title: "Out for Delivery",
          location: `${destinationCity} Center`,
          timestamp: "Pending",
          description: "Assigned to Supply Chain runner.",
          completed: false,
        },
        {
          status: "delivered",
          title: "Delivered",
          location: `${destinationCity} Home`,
          timestamp: "Pending",
          description: "Awaiting signature verification.",
          completed: false,
        },
      ],
    };

    // Register shipment on parent state
    onAddShipment(bookedShipment);

    // Create wallet transaction ledger if prepaid
    if (paymentType === "Prepaid") {
      const debitTxn = {
        id: `TXN-${Math.floor(Math.random() * 900000) + 100000}`,
        date: new Date().toLocaleString(),
        type: "debit",
        amount: rateEstimate.total,
        description: `Booking charge paid for AWB ${newAwb}`,
        status: "success",
      };
      setTransactions((prev) => [debitTxn, ...prev]);
    }

    // Set printable label popup modal
    setBookedLabel(bookedShipment);

    // Reset receiver fields
    setReceiverName("");
    setReceiverMobile("");
    setReceiverAddress("");
    setCodAmount(0);
  };

  // Filter local booked list
  const filteredShipments = shipments.filter(
    (s) =>
      s.awb.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.receiverName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Aggregate stats
  const activeShipmentsCount = shipments.filter(
    (s) => s.status !== "delivered" && s.status !== "failed",
  ).length;
  const deliveredCount = shipments.filter(
    (s) => s.status === "delivered",
  ).length;
  const pendingCodAmount = shipments
    .filter((s) => s.paymentType === "COD" && s.status !== "delivered")
    .reduce((sum, s) => sum + s.codAmount, 0);

  // Mock chart data representing merchant shipping costs and collections over a week
  const shippingMetricsData = [
    { day: "Mon", PrepaidFares: 2450, CODCollected: 1450, Volume: 5 },
    { day: "Tue", PrepaidFares: 3100, CODCollected: 0, Volume: 8 },
    { day: "Wed", PrepaidFares: 1800, CODCollected: 2900, Volume: 4 },
    { day: "Thu", PrepaidFares: 4200, CODCollected: 1200, Volume: 11 },
    { day: "Fri", PrepaidFares: 2900, CODCollected: 3500, Volume: 7 },
    { day: "Sat", PrepaidFares: 1500, CODCollected: 850, Volume: 3 },
    { day: "Sun", PrepaidFares: 900, CODCollected: 0, Volume: 2 },
  ];

  return (
    <div className="flex-1 bg-slate-50 min-h-screen">
      {/* Merchant Title Header */}
      <div className="bg-[#0b1532] text-white py-12 px-4 border-b border-slate-900">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-sans font-black tracking-tight text-white flex items-center gap-3">
              <Building className="w-8 h-8 text-blue-500" />
              <span>Merchant Console</span>
            </h1>
            <p className="text-slate-400 text-sm max-w-xl">
              Fulfill bulk consumer bookings, top up digital shipping wallets,
              compute rate estimates, and check real-time reverse Supply Chain
              ledger instantly.
            </p>
          </div>

          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 flex items-center gap-4 shrink-0 shadow">
            <div className="p-3 bg-blue-950 text-blue-400 rounded-xl">
              <Wallet className="w-6 h-6" />
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase text-slate-400 tracking-wider">
                Active Shipping Wallet
              </p>
              <p className="text-xl font-bold text-slate-100 mt-0.5">
                ₹{walletBalance.toLocaleString()}
              </p>
            </div>
            <button
              onClick={() => setActiveSubTab("wallet")}
              className="ml-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-xs font-semibold px-3.5 py-2 rounded-xl transition-colors cursor-pointer"
            >
              Top Up
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="border-b border-slate-200 bg-white sticky top-[132px] z-40">
        <div className="max-w-7xl mx-auto px-4 flex gap-6 overflow-x-auto scrollbar-none">
          <button
            onClick={() => setActiveSubTab("overview")}
            className={`py-4 px-2 text-xs font-bold uppercase tracking-wider relative flex items-center gap-1.5 transition-colors cursor-pointer ${
              activeSubTab === "overview"
                ? "text-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Analytics Overview
            {activeSubTab === "overview" && (
              <motion.div
                layoutId="subTabUnderline"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
              />
            )}
          </button>

          <button
            onClick={() => setActiveSubTab("booking")}
            className={`py-4 px-2 text-xs font-bold uppercase tracking-wider relative flex items-center gap-1.5 transition-colors cursor-pointer ${
              activeSubTab === "booking"
                ? "text-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <PlusCircle className="w-4 h-4" />
            Book Shipment / Rate Card
            {activeSubTab === "booking" && (
              <motion.div
                layoutId="subTabUnderline"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
              />
            )}
          </button>

          <button
            onClick={() => setActiveSubTab("wallet")}
            className={`py-4 px-2 text-xs font-bold uppercase tracking-wider relative flex items-center gap-1.5 transition-colors cursor-pointer ${
              activeSubTab === "wallet"
                ? "text-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Coins className="w-4 h-4" />
            Ledger & Billing
            {activeSubTab === "wallet" && (
              <motion.div
                layoutId="subTabUnderline"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
              />
            )}
          </button>

          <button
            onClick={() => setActiveSubTab("api")}
            className={`py-4 px-2 text-xs font-bold uppercase tracking-wider relative flex items-center gap-1.5 transition-colors cursor-pointer ${
              activeSubTab === "api"
                ? "text-blue-600"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Terminal className="w-4 h-4" />
            Developer API
            {activeSubTab === "api" && (
              <motion.div
                layoutId="subTabUnderline"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
              />
            )}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* TAB 1: ANALYTICS OVERVIEW */}
        {activeSubTab === "overview" && (
          <div className="space-y-8">
            {/* Bento Statistics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-white border border-slate-200 p-5 rounded-2xl flex items-center gap-4 shadow-sm">
                <div className="p-3.5 bg-indigo-50 text-indigo-600 rounded-xl">
                  <PackageCheck className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
                    Total Bookings
                  </p>
                  <p className="text-2xl font-black text-slate-900 mt-0.5">
                    {shipments.length}
                  </p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-5 rounded-2xl flex items-center gap-4 shadow-sm">
                <div className="p-3.5 bg-blue-50 text-blue-600 rounded-xl">
                  <Truck className="w-6 h-6 animate-pulse" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
                    Active Transits
                  </p>
                  <p className="text-2xl font-black text-slate-900 mt-0.5">
                    {activeShipmentsCount}
                  </p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-5 rounded-2xl flex items-center gap-4 shadow-sm">
                <div className="p-3.5 bg-emerald-50 text-emerald-600 rounded-xl">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
                    Delivered SLA
                  </p>
                  <p className="text-2xl font-black text-slate-900 mt-0.5">
                    {deliveredCount}
                  </p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-5 rounded-2xl flex items-center gap-4 shadow-sm">
                <div className="p-3.5 bg-amber-50 text-amber-600 rounded-xl">
                  <FileSpreadsheet className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
                    Pending COD Clearance
                  </p>
                  <p className="text-2xl font-black text-slate-900 mt-0.5">
                    ₹{pendingCodAmount.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Recharts Shipping Trends Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="font-sans font-bold text-base text-slate-900">
                      COD Collected vs Prepaid Shipping Costs
                    </h3>
                    <p className="text-xs text-slate-500">
                      Compare daily digital shipping fees against
                      cash-on-delivery merchant balances.
                    </p>
                  </div>
                  <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded font-semibold uppercase tracking-wider">
                    Weekly Log
                  </span>
                </div>

                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={shippingMetricsData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorPrepaid"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#3b82f6"
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="95%"
                            stopColor="#3b82f6"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorCOD"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#10b981"
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="95%"
                            stopColor="#10b981"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e2e8f0"
                      />
                      <XAxis
                        dataKey="day"
                        stroke="#94a3b8"
                        fontSize={11}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        iconType="circle"
                      />
                      <Area
                        type="monotone"
                        dataKey="PrepaidFares"
                        name="Prepaid Fares Paid"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorPrepaid)"
                      />
                      <Area
                        type="monotone"
                        dataKey="CODCollected"
                        name="COD Remittance Received"
                        stroke="#10b981"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorCOD)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="lg:col-span-4 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <h3 className="font-sans font-bold text-base text-slate-900 mb-2">
                    Weekly Freight Load
                  </h3>
                  <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                    Daily packet booking counts managed under JPSY fulfillment
                    warehouses.
                  </p>
                </div>

                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={shippingMetricsData}
                      margin={{ top: 0, right: 0, left: -30, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f1f5f9"
                      />
                      <XAxis
                        dataKey="day"
                        stroke="#94a3b8"
                        fontSize={11}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip />
                      <Bar
                        dataKey="Volume"
                        name="Packages Dispatched"
                        fill="#6366f1"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="border-t border-slate-100 pt-4 mt-4 text-[11px] text-slate-400 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>
                    SLA on-time compliance stands at 99.82% over this period.
                  </span>
                </div>
              </div>
            </div>

            {/* Local Booked Shipment List */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                <div>
                  <h3 className="font-sans font-bold text-base text-slate-900">
                    Registered Shipment Consignments
                  </h3>
                  <p className="text-xs text-slate-500">
                    List of packages booked under this merchant ID. Type AWB to
                    filter.
                  </p>
                </div>

                <div className="relative max-w-xs w-full">
                  <Search className="absolute inset-y-0 left-3 my-auto w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search AWB or receiver..."
                    className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs text-slate-800"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50 text-slate-500 font-mono text-[10px] uppercase tracking-wider">
                      <th className="py-3 px-4 font-semibold">AWB Details</th>
                      <th className="py-3 px-4 font-semibold">
                        Receiver & Dest
                      </th>
                      <th className="py-3 px-4 font-semibold">SLA Class</th>
                      <th className="py-3 px-4 font-semibold">Payment Mode</th>
                      <th className="py-3 px-4 font-semibold">Status</th>
                      <th className="py-3 px-4 font-semibold text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredShipments.map((s) => (
                      <tr key={s.awb} className="hover:bg-slate-50/50">
                        <td className="py-3.5 px-4">
                          <p className="font-bold text-slate-800 font-mono">
                            {s.awb}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            Order: {s.orderId}
                          </p>
                        </td>
                        <td className="py-3.5 px-4">
                          <p className="font-semibold text-slate-800">
                            {s.receiverName}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {s.destinationCity} ({s.destinationPincode})
                          </p>
                        </td>
                        <td className="py-3.5 px-4">
                          <p className="font-medium text-slate-700">
                            {s.serviceType}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {s.weight} kg
                          </p>
                        </td>
                        <td className="py-3.5 px-4">
                          <p className="font-semibold text-slate-800">
                            {s.paymentType}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {s.paymentType === "COD"
                              ? `₹${s.codAmount}`
                              : "Digital Prepay"}
                          </p>
                        </td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${
                              s.status === "delivered"
                                ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                : s.status === "in_transit" ||
                                    s.status === "out_for_delivery"
                                  ? "bg-blue-100 text-blue-800 border border-blue-200"
                                  : "bg-slate-100 text-slate-800 border border-slate-200"
                            }`}
                          >
                            {s.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <button
                            onClick={() => {
                              // Deep-track that specific item on home
                              const event = new CustomEvent("jpsy-navigate", {
                                detail: { tab: "home" },
                              });
                              window.dispatchEvent(event);
                              onTabChange("home");
                              // Automatically load that shipment
                              setTimeout(() => {
                                const trEvent = new CustomEvent(
                                  "jpsy-track-item",
                                  { detail: { shipment: s } },
                                );
                                window.dispatchEvent(trEvent);
                              }, 100);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-bold hover:underline cursor-pointer inline-flex items-center gap-0.5"
                          >
                            <span>Live Track</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredShipments.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-8 text-center text-slate-400 text-xs"
                        >
                          No active merchant consignments match this search
                          query.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: BOOK SHIPMENT / CALCULATOR */}
        {activeSubTab === "booking" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Booking Form (7 Columns) */}
            <form
              onSubmit={handleBookShipment}
              className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-6"
            >
              <h3 className="font-sans font-black text-lg text-slate-900 border-b border-slate-100 pb-3 flex items-center gap-2">
                <PlusCircle className="w-5.5 h-5.5 text-blue-600" />
                Register New Cargo Shipment
              </h3>

              {/* Sender Details */}
              <div className="space-y-4">
                <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wider">
                  1. Pickup Warehouse / Sender Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Sender Name
                    </label>
                    <input
                      type="text"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Contact Number
                    </label>
                    <input
                      type="text"
                      value={senderMobile}
                      onChange={(e) => setSenderMobile(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Warehouse Address
                    </label>
                    <input
                      type="text"
                      value={senderAddress}
                      onChange={(e) => setSenderAddress(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Pickup Pincode
                    </label>
                    <input
                      type="text"
                      maxLength={6}
                      value={originPincode}
                      onChange={(e) =>
                        setOriginPincode(e.target.value.replace(/\D/g, ""))
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Receiver Details */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wider">
                  2. Destination / Consignee Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Receiver Name *
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="Enter customer name"
                      value={receiverName}
                      onChange={(e) => setReceiverName(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Consignee Mobile *
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="e.g. 9123456789"
                      value={receiverMobile}
                      onChange={(e) => setReceiverMobile(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Shipping Address *
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="Street, locality, landmarks"
                      value={receiverAddress}
                      onChange={(e) => setReceiverAddress(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Destination Pincode *
                    </label>
                    <input
                      required
                      type="text"
                      maxLength={6}
                      placeholder="e.g. 400050"
                      value={destinationPincode}
                      onChange={(e) =>
                        setDestinationPincode(e.target.value.replace(/\D/g, ""))
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Package Details */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wider">
                  3. Commodity Specifications & Service
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Gross Weight (kg)
                    </label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={weight}
                      onChange={(e) =>
                        setWeight(parseFloat(e.target.value) || 1.0)
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Supply Chain Tier
                    </label>
                    <select
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    >
                      <option value="Express Parcel">Express Parcel</option>
                      <option value="Part Truckload">Part Truckload</option>
                      <option value="Full Truckload">Full Truckload</option>
                      <option value="International">
                        International Shipping
                      </option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5 text-xs">
                    <label className="font-semibold text-slate-500">
                      Payment Mode
                    </label>
                    <select
                      value={paymentType}
                      onChange={(e) => setPaymentType(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    >
                      <option value="Prepaid">Prepaid (Wallet Debit)</option>
                      <option value="COD">Cash on Delivery (COD)</option>
                    </select>
                  </div>
                </div>

                {paymentType === "COD" && (
                  <div className="flex flex-col gap-1.5 text-xs max-w-xs">
                    <label className="font-semibold text-slate-500">
                      Cash collection amount (₹) *
                    </label>
                    <input
                      required
                      type="number"
                      min="1"
                      value={codAmount}
                      onChange={(e) =>
                        setCodAmount(parseFloat(e.target.value) || 0)
                      }
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 pt-6 flex justify-end gap-3">
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-sm px-6 py-3 rounded-xl transition-all shadow-md cursor-pointer flex items-center gap-1.5"
                >
                  <Barcode className="w-5 h-5" />
                  <span>Book Shipment & Print Label</span>
                </button>
              </div>
            </form>

            {/* Dynamic ledger calculation card (5 Columns) */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-[#0b1532] text-white border border-slate-900 rounded-2xl p-6 shadow-xl space-y-5">
                <h3 className="font-sans font-bold text-sm uppercase tracking-wider text-slate-300 flex items-center gap-2 border-b border-slate-800 pb-2">
                  <Calculator className="w-4.5 h-4.5 text-blue-400" />
                  Dynamic Fare Ledger
                </h3>

                <div className="space-y-3.5 text-xs">
                  <div className="flex justify-between text-slate-400">
                    <span>Base Freight Charge</span>
                    <span className="font-mono">₹{rateEstimate.baseFare}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Fuel Surcharge (12%)</span>
                    <span className="font-mono">
                      ₹{rateEstimate.fuelSurcharge}
                    </span>
                  </div>
                  {paymentType === "COD" && (
                    <div className="flex justify-between text-slate-400">
                      <span>COD Collection Surcharge</span>
                      <span className="font-mono">
                        ₹{rateEstimate.codCharges}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-400">
                    <span>Central GST (18%)</span>
                    <span className="font-mono">₹{rateEstimate.gst}</span>
                  </div>

                  <div className="border-t border-slate-800 my-2 pt-3 flex justify-between text-sm font-bold">
                    <span className="text-white">Est. Total Charges</span>
                    <span className="font-mono text-blue-400">
                      ₹{rateEstimate.total}
                    </span>
                  </div>
                </div>

                <div className="bg-slate-950/60 rounded-xl p-3.5 text-[11px] text-slate-300 space-y-2 border border-slate-850">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span>
                      Estimated delivery time:{" "}
                      <strong className="text-white">
                        {rateEstimate.etaDays} days
                      </strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                    <span>Prepaid bookings require instant wallet debits.</span>
                  </div>
                </div>
              </div>

              {/* Sample print instructions */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 text-xs text-slate-500 leading-relaxed font-sans space-y-2">
                <p className="font-bold text-slate-800 uppercase tracking-wider text-[10px]">
                  Thermal Printing Instructions
                </p>
                <p>
                  The label printed from JPSY is standard 4x6 inches dimension.
                  Paste the label securely on the outer box without creases over
                  the barcoded segment to prevent sorting mis-scans.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: LEDGER & BILLING */}
        {activeSubTab === "wallet" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Top Up Wallet Form (5 Columns) */}
            <div className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5">
              <h3 className="font-sans font-bold text-base text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-3">
                <Wallet className="w-5 h-5 text-blue-600" />
                Wallet Quick Top Up
              </h3>

              <form onSubmit={handleTopUp} className="space-y-4">
                <div className="flex flex-col gap-1.5 text-xs">
                  <label className="font-semibold text-slate-500">
                    Recharge Amount (INR)
                  </label>
                  <input
                    required
                    type="number"
                    min="500"
                    placeholder="Enter amount (Min ₹500)"
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-slate-800 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono font-bold"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-xs font-bold py-3 px-4 rounded-xl transition-all shadow cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <Coins className="w-4.5 h-4.5" />
                  <span>Initiate UPI Recharge</span>
                </button>
              </form>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-[11px] text-slate-500 space-y-2 leading-relaxed">
                <p className="font-semibold text-slate-700">
                  Digital Gateway SLA:
                </p>
                <p>
                  Recharges are updated within 5 seconds on our server sorting
                  machines. In case of UPI banking delays, transaction logs will
                  auto-recon within 2 hours.
                </p>
              </div>
            </div>

            {/* Ledger sheet (7 Columns) */}
            <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <h3 className="font-sans font-bold text-base text-slate-900 border-b border-slate-100 pb-3 mb-5">
                Historical Wallet Ledger
              </h3>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400 font-mono text-[9px] uppercase tracking-wider">
                      <th className="py-2 pb-3">Transaction ID</th>
                      <th className="py-2 pb-3">Date & Time</th>
                      <th className="py-2 pb-3">Type</th>
                      <th className="py-2 pb-3">Narration</th>
                      <th className="py-2 pb-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-600">
                    {transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td className="py-3.5 font-mono font-bold text-slate-700">
                          {tx.id}
                        </td>
                        <td className="py-3.5 text-slate-400">{tx.date}</td>
                        <td className="py-3.5">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold ${
                              tx.type === "credit"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                : "bg-slate-50 text-slate-600 border border-slate-100"
                            }`}
                          >
                            {tx.type}
                          </span>
                        </td>
                        <td className="py-3.5 max-w-xs truncate">
                          {tx.description}
                        </td>
                        <td
                          className={`py-3.5 text-right font-mono font-bold ${tx.type === "credit" ? "text-emerald-600" : "text-slate-800"}`}
                        >
                          {tx.type === "credit" ? "+" : "-"}₹{tx.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: DEVELOPER API DOCS */}
        {activeSubTab === "api" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-white shadow-xl space-y-6">
            <div className="space-y-1">
              <h3 className="text-lg font-bold font-sans flex items-center gap-2">
                <Terminal className="w-5.5 h-5.5 text-blue-500 animate-pulse" />
                JPSY Supply Chain API Integrations
              </h3>
              <p className="text-xs text-slate-400">
                Automate high-volume parcel manifestations and label downloads
                from your internal e-commerce engine.
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="text-xs font-mono font-semibold text-blue-400 uppercase tracking-widest border-b border-slate-800 pb-1.5">
                1. manifest_shipment Endpoint
              </h4>
              <p className="text-xs text-slate-300">
                Trigger standard JSON POST payload to manifest air-freight
                labels.
              </p>

              <div className="bg-slate-950 border border-slate-850 rounded-xl p-4 font-mono text-[11px] overflow-x-auto text-slate-300 relative">
                <span className="absolute top-2 right-2 text-[9px] bg-blue-950 text-blue-400 font-mono px-2 py-0.5 rounded border border-blue-900/50">
                  Node.js
                </span>
                <pre>{`const axios = require('axios');

const config = {
  method: 'post',
  url: 'https://api.jpsy-logistics.com/v1/shipments/manifest',
  headers: { 
    'Authorization': 'Bearer jpsy_live_key_99881122aaff', 
    'Content-Type': 'application/json'
  },
  data: {
    origin_pincode: "560100",
    destination_pincode: "400050",
    gross_weight_kg: 1.5,
    service_tier: "Express Parcel",
    cod_charge_amount: 0,
    consignee: {
      name: "Rohan Sharma",
      phone: "+919123456789",
      address_line1: "Flat 402, Sea Breeze, Bandra West",
      city: "Mumbai"
    }
  }
};

axios(config)
  .then(res => console.log('AWB Manifested:', res.data.awb))
  .catch(err => console.error(err));`}</pre>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PRINTABLE BARCODED LABEL POPUP MODAL */}
      <AnimatePresence>
        {bookedLabel && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white text-slate-900 border-2 border-slate-900 rounded-xl max-w-sm w-full p-6 shadow-2xl relative"
            >
              <button
                onClick={() => setBookedLabel(null)}
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-800 p-1.5 rounded-full hover:bg-slate-100"
              >
                ×
              </button>

              <div className="text-center pb-4 border-b border-dashed border-slate-300">
                <Logo className="mx-auto block h-auto w-40" />
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                  Express Airway Bill Label
                </p>
              </div>

              {/* Barcode representation */}
              <div className="py-6 flex flex-col items-center justify-center border-b border-dashed border-slate-300">
                {/* SVG simulated crisp code128 barcode */}
                <svg viewBox="0 0 100 40" className="w-48 h-12">
                  <rect x="2" y="5" width="2" height="30" fill="black" />
                  <rect x="6" y="5" width="1" height="30" fill="black" />
                  <rect x="9" y="5" width="3" height="30" fill="black" />
                  <rect x="14" y="5" width="1" height="30" fill="black" />
                  <rect x="17" y="5" width="2" height="30" fill="black" />
                  <rect x="21" y="5" width="4" height="30" fill="black" />
                  <rect x="27" y="5" width="1" height="30" fill="black" />
                  <rect x="30" y="5" width="2" height="30" fill="black" />
                  <rect x="34" y="5" width="3" height="30" fill="black" />
                  <rect x="39" y="5" width="1" height="30" fill="black" />
                  <rect x="42" y="5" width="4" height="30" fill="black" />
                  <rect x="48" y="5" width="1" height="30" fill="black" />
                  <rect x="51" y="5" width="2" height="30" fill="black" />
                  <rect x="55" y="5" width="3" height="30" fill="black" />
                  <rect x="60" y="5" width="1" height="30" fill="black" />
                  <rect x="63" y="5" width="2" height="30" fill="black" />
                  <rect x="67" y="5" width="4" height="30" fill="black" />
                  <rect x="73" y="5" width="1" height="30" fill="black" />
                  <rect x="76" y="5" width="2" height="30" fill="black" />
                  <rect x="80" y="5" width="3" height="30" fill="black" />
                  <rect x="85" y="5" width="1" height="30" fill="black" />
                  <rect x="88" y="5" width="4" height="30" fill="black" />
                  <rect x="94" y="5" width="2" height="30" fill="black" />
                </svg>
                <p className="font-mono text-xs font-bold mt-1 tracking-widest">
                  {bookedLabel.awb}
                </p>
              </div>

              {/* Sender/Receiver details for the print list */}
              <div className="py-4 space-y-3.5 text-[11px] border-b border-dashed border-slate-300 font-sans">
                <div>
                  <p className="font-mono text-[9px] uppercase text-slate-500 tracking-wider">
                    Sender Warehouse
                  </p>
                  <p className="font-bold text-slate-800">
                    {bookedLabel.senderName}
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    {bookedLabel.senderAddress}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[9px] uppercase text-slate-500 tracking-wider">
                    Ship To (Consignee)
                  </p>
                  <p className="font-bold text-slate-800">
                    {bookedLabel.receiverName} (Ph: {bookedLabel.receiverMobile}
                    )
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    {bookedLabel.receiverAddress}
                  </p>
                </div>
              </div>

              {/* Lower dispatch tags */}
              <div className="pt-4 grid grid-cols-2 gap-4 text-[10px] font-sans">
                <div>
                  <p className="text-slate-400 uppercase tracking-wider">
                    Supply Chain Tier
                  </p>
                  <p className="font-bold text-slate-800">
                    {bookedLabel.serviceType}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 uppercase tracking-wider">
                    Weight Class
                  </p>
                  <p className="font-bold text-slate-800 font-mono">
                    {bookedLabel.weight} KG
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 uppercase tracking-wider">
                    Payment Mode
                  </p>
                  <p className="font-bold text-slate-800">
                    {bookedLabel.paymentType}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 uppercase tracking-wider">
                    Routing Code
                  </p>
                  <p className="font-bold text-slate-800 font-mono">
                    {bookedLabel.originCity.slice(0, 3).toUpperCase()} →{" "}
                    {bookedLabel.destinationCity.slice(0, 3).toUpperCase()}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex gap-3 text-xs">
                <button
                  onClick={() => {
                    alert(
                      "Simulating direct print queue dispatch. Your thermal label is printing.",
                    );
                    setBookedLabel(null);
                  }}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 px-4 rounded-lg text-center"
                >
                  Print thermal label
                </button>
                <button
                  onClick={() => setBookedLabel(null)}
                  className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-4 rounded-lg text-center"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
